import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useBusinessLine, BUSINESS_LINES } from '../contexts/BusinessLineContext';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Checkbox } from './ui/checkbox';
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';
import { Separator } from './ui/separator';
import { toast } from 'sonner';
import { Plus, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Campo reutilizables ─────────────────────────────────────────────────────
const F_PRIMA_FREQ = { key: 'prima_frecuencia', label: 'Frecuencia de pago', type: 'select', options: ['mensual', 'trimestral', 'semestral', 'anual'], required: true };
const F_INVALIDEZ  = { key: 'invalidez_total', label: 'Cobertura invalidez total y permanente', type: 'checkbox', required: false };
const F_M_ACCID    = { key: 'muerte_accidental', label: 'Muerte accidental', type: 'checkbox', required: false };
const F_MONEDA     = { key: 'moneda', label: 'Moneda', type: 'select', options: ['MXN', 'USD'], required: false };
const F_DEV_PRIM   = { key: 'devolucion_primas', label: 'Devolución de primas', type: 'checkbox', required: false };

// ─── Configuración de productos por aseguradora ──────────────────────────────
export const POLICY_CONFIG = {
  allianz: {
    label: 'Allianz',
    products: [
      {
        group: 'Ahorro e Inversión',
        name: 'OptiMaxx Plus',
        hasBeneficiarios: true,
        fields: [
          { key: 'plazo', label: 'Plazo contratado (años)', type: 'number', min: 5, max: 25, required: true, placeholder: 'Ej: 15' },
          F_PRIMA_FREQ,
          { key: 'suma_asegurada_fallecimiento', label: 'Suma asegurada por fallecimiento', type: 'number', required: false },
          F_INVALIDEZ,
          F_DEV_PRIM,
          { key: 'muerte_accidental_suma', label: 'Muerte accidental — suma adicional', type: 'number', required: false },
          { key: 'portafolio', label: 'Portafolio de inversión seleccionado', type: 'text', required: false },
          F_MONEDA,
        ],
      },
      {
        group: 'Ahorro e Inversión',
        name: 'Ahorro Educación',
        hasBeneficiarios: false,
        fields: [
          { key: 'nombre_menor', label: 'Nombre del menor beneficiado', type: 'text', required: true },
          { key: 'fecha_nacimiento_menor', label: 'Fecha de nacimiento del menor', type: 'date', required: true },
          { key: 'plazo_hasta_edad', label: 'Plazo — hasta qué edad del menor', type: 'text', required: true, placeholder: 'Ej: hasta los 18 años' },
          F_PRIMA_FREQ,
          { key: 'suma_objetivo_termino', label: 'Suma objetivo al término', type: 'number', required: true },
          { key: 'beneficiario_sustituto', label: 'Beneficiario sustituto si el contratante fallece', type: 'text', required: true },
          { key: 'cobertura_fallecimiento_contratante', label: 'Cobertura por fallecimiento del contratante', type: 'checkbox', required: false },
          { key: 'exencion_primas_invalidez', label: 'Exención de primas por invalidez', type: 'checkbox', required: false },
        ],
      },
      {
        group: 'Ahorro e Inversión',
        name: 'Inversión Elite',
        hasBeneficiarios: true,
        fields: [
          { key: 'tipo_prima', label: 'Tipo de aportación', type: 'select', options: ['Prima única', 'Aportaciones periódicas'], required: true },
          { key: 'portafolios', label: 'Portafolio(s) seleccionado(s)', type: 'text', required: true },
          { key: 'plazo_permanencia', label: 'Plazo mínimo de permanencia', type: 'text', required: true },
          { key: 'suma_asegurada_fallecimiento', label: 'Suma asegurada por fallecimiento', type: 'number', required: false },
          F_MONEDA,
          { key: 'saldo_acumulado', label: 'Valor de la póliza / saldo acumulado', type: 'number', required: false },
        ],
      },
      {
        group: 'Ahorro e Inversión',
        name: 'Inversión Patrimonial',
        hasBeneficiarios: true,
        fields: [
          { key: 'tipo_prima', label: 'Tipo de aportación', type: 'select', options: ['Prima única', 'Aportaciones periódicas'], required: true },
          { key: 'portafolios', label: 'Portafolio(s) seleccionado(s)', type: 'text', required: true },
          { key: 'plazo_permanencia', label: 'Plazo mínimo de permanencia', type: 'text', required: true },
          { key: 'suma_asegurada_fallecimiento', label: 'Suma asegurada por fallecimiento', type: 'number', required: false },
          F_MONEDA,
          { key: 'saldo_acumulado', label: 'Valor de la póliza / saldo acumulado', type: 'number', required: false },
        ],
      },
      {
        group: 'Seguros Personales',
        name: 'Vida Individual',
        hasBeneficiarios: true,
        fields: [
          { key: 'tipo_plan', label: 'Tipo de plan', type: 'select', options: ['temporal', 'vitalicio', 'dotal'], required: true },
          { key: 'plazo_contratado', label: 'Plazo contratado', type: 'text', required: true },
          { key: 'suma_asegurada_principal', label: 'Suma asegurada principal', type: 'number', required: true },
          F_PRIMA_FREQ,
          F_INVALIDEZ,
          F_M_ACCID,
          { key: 'perdidas_organicas', label: 'Pérdidas orgánicas', type: 'checkbox', required: false },
          F_DEV_PRIM,
          { key: 'exencion_pago_invalidez', label: 'Exención de pago por invalidez', type: 'checkbox', required: false },
        ],
      },
      {
        group: 'Seguros Personales',
        name: 'Gastos Médicos Mayores',
        hasBeneficiarios: false,
        fields: [
          { key: 'suma_asegurada', label: 'Suma asegurada', type: 'number', required: true },
          { key: 'deducible', label: 'Deducible', type: 'number', required: true },
          { key: 'coaseguro_pct', label: 'Coaseguro % a cargo del asegurado', type: 'number', required: true },
          { key: 'tope_coaseguro', label: 'Tope de coaseguro', type: 'number', required: true },
          { key: 'nivel_hospitalario', label: 'Nivel hospitalario', type: 'select', options: ['básico', 'intermedio', 'premium'], required: true },
          { key: 'titulares_edades', label: 'Titulares asegurados y edades', type: 'textarea', required: true, placeholder: 'Ej: Juan (35), María (33), Niño (5)' },
          { key: 'cobertura_maternidad', label: 'Cobertura maternidad', type: 'checkbox', required: false },
          { key: 'cobertura_dental_visual', label: 'Cobertura dental / visual', type: 'checkbox', required: false },
          { key: 'cobertura_extranjero', label: 'Cobertura en el extranjero', type: 'checkbox', required: false },
          { key: 'red_medica_preferente', label: 'Red médica preferente', type: 'text', required: false },
        ],
      },
      {
        group: 'Seguros Personales',
        name: 'Auto Allianz',
        hasBeneficiarios: false,
        fields: [
          { key: 'nivel_cobertura', label: 'Nivel de cobertura', type: 'select', options: ['RC', 'Limitada', 'Amplia', 'Amplia Plus'], required: true },
          { key: 'marca', label: 'Marca', type: 'text', required: true },
          { key: 'modelo', label: 'Modelo', type: 'text', required: true },
          { key: 'anio', label: 'Año', type: 'number', required: true },
          { key: 'version', label: 'Versión', type: 'text', required: true },
          { key: 'vin', label: 'Número de serie (VIN)', type: 'text', required: true },
          { key: 'placas', label: 'Placas', type: 'text', required: true },
          { key: 'suma_asegurada_vehiculo', label: 'Suma asegurada del vehículo', type: 'number', required: true },
          { key: 'deducible_danos', label: 'Deducible por daños', type: 'number', required: true },
          { key: 'deducible_robo', label: 'Deducible por robo', type: 'number', required: true },
          { key: 'rc_mxn', label: 'Cobertura RC — monto MXN', type: 'number', required: true },
          { key: 'rc_usd', label: 'Cobertura RC — monto USD', type: 'number', required: true },
          { key: 'auto_sustituto_dias', label: 'Auto sustituto — días incluidos', type: 'number', required: false },
          { key: 'reparacion_agencia', label: 'Reparación en agencia', type: 'checkbox', required: false },
          { key: 'asistencia_vial_ilimitada', label: 'Asistencia vial ilimitada', type: 'checkbox', required: false },
          { key: 'cobertura_extranjero', label: 'Cobertura en el extranjero', type: 'checkbox', required: false },
        ],
      },
      {
        group: 'Seguros Personales',
        name: 'Hogar / Residencial Premier',
        hasBeneficiarios: false,
        fields: [
          { key: 'direccion_inmueble', label: 'Dirección del inmueble asegurado', type: 'text', required: true },
          { key: 'tipo_propiedad', label: 'Tipo de propiedad', type: 'select', options: ['casa', 'departamento', 'otro'], required: true },
          { key: 'suma_asegurada_construccion', label: 'Suma asegurada — construcción', type: 'number', required: true },
          { key: 'suma_asegurada_contenido', label: 'Suma asegurada — contenido', type: 'number', required: true },
          { key: 'cobertura_incendio', label: 'Cobertura incendio', type: 'checkbox', required: true },
          { key: 'cobertura_robo', label: 'Cobertura robo', type: 'checkbox', required: true },
          { key: 'cobertura_rc_familiar', label: 'Cobertura RC familiar', type: 'checkbox', required: true },
          { key: 'cobertura_sismo', label: 'Cobertura sismo', type: 'checkbox', required: true },
          { key: 'deducible', label: 'Deducible', type: 'number', required: false },
          { key: 'coberturas_adicionales', label: 'Coberturas adicionales', type: 'textarea', required: false },
        ],
      },
      {
        group: 'Empresas',
        name: 'Vida Grupo / GMM Empresarial',
        hasBeneficiarios: false,
        fields: [
          { key: 'razon_social', label: 'Razón social del contratante', type: 'text', required: true },
          { key: 'num_vidas', label: 'Número de vidas aseguradas', type: 'number', required: true },
          { key: 'suma_asegurada_empleado', label: 'Suma asegurada por empleado', type: 'number', required: true },
          { key: 'prima_total_anual', label: 'Prima total anual', type: 'number', required: true },
          { key: 'coberturas_adicionales', label: 'Coberturas adicionales', type: 'textarea', required: false },
        ],
      },
    ],
  },
  insignia: {
    label: 'Insignia Life',
    products: [
      {
        group: 'Seguros Personales',
        name: 'DotIL / Para Vivir',
        hasBeneficiarios: true,
        fields: [
          { key: 'tipo_plan', label: 'Tipo de plan', type: 'select', options: ['DotIL', 'Destino Seguro', 'Premia', 'otro'], required: true },
          { key: 'plazo_contratado', label: 'Plazo contratado', type: 'text', required: true },
          { key: 'suma_asegurada_fallecimiento', label: 'Suma asegurada por fallecimiento', type: 'number', required: false },
          F_PRIMA_FREQ,
          { key: 'suma_ahorro_garantizada', label: 'Suma de ahorro garantizada al término', type: 'number', required: false },
          F_INVALIDEZ,
          F_M_ACCID,
          { key: 'asistencia_funeraria', label: 'Asistencia funeraria', type: 'checkbox', required: false },
          F_MONEDA,
        ],
      },
      {
        group: 'Seguros Personales',
        name: 'Para Vivir Temporal',
        hasBeneficiarios: true,
        fields: [
          { key: 'plazo', label: 'Plazo', type: 'select', options: ['1 año', '5 años', '10 años', '20 años'], required: true },
          { key: 'suma_asegurada', label: 'Suma asegurada', type: 'number', required: true },
          { key: 'prima_anual', label: 'Prima anual', type: 'number', required: true },
          F_INVALIDEZ,
          F_M_ACCID,
        ],
      },
      {
        group: 'Seguros Personales',
        name: 'Seguro Vitalicio / Vida Noventa',
        hasBeneficiarios: true,
        fields: [
          { key: 'suma_asegurada', label: 'Suma asegurada', type: 'number', required: true },
          { key: 'periodo_pago', label: 'Prima periódica — período de pago', type: 'text', required: true },
          { key: 'valor_rescate', label: 'Valor de rescate acumulado', type: 'number', required: true },
          { key: 'prestamos_automaticos', label: 'Préstamos automáticos u ordinarios', type: 'checkbox', required: false },
          { key: 'coberturas_adicionales', label: 'Coberturas adicionales', type: 'textarea', required: false },
        ],
      },
      {
        group: 'Seguros Personales',
        name: 'Retiro / Promesa Académica',
        hasBeneficiarios: true,
        fields: [
          { key: 'objetivo', label: 'Objetivo', type: 'select', options: ['retiro', 'educación'], required: true },
          { key: 'plazo_contratado', label: 'Plazo contratado', type: 'text', required: true },
          F_PRIMA_FREQ,
          { key: 'suma_garantizada_termino', label: 'Suma garantizada al término', type: 'number', required: true },
          { key: 'cobertura_fallecimiento', label: 'Cobertura por fallecimiento del contratante', type: 'checkbox', required: false },
          { key: 'exencion_primas_invalidez', label: 'Exención de primas por invalidez', type: 'checkbox', required: false },
        ],
      },
      {
        group: 'Seguros Empresariales',
        name: 'PyMES / Vida Grupo',
        hasBeneficiarios: false,
        fields: [
          { key: 'razon_social', label: 'Razón social', type: 'text', required: true },
          { key: 'num_empleados', label: 'Número de empleados asegurados', type: 'number', required: true },
          { key: 'suma_asegurada_empleado', label: 'Suma asegurada por empleado', type: 'number', required: true },
          { key: 'prima_total', label: 'Prima total', type: 'number', required: true },
          F_INVALIDEZ,
        ],
      },
      {
        group: 'Seguros Empresariales',
        name: 'Persona Clave / Socio',
        hasBeneficiarios: false,
        fields: [
          { key: 'nombre_asegurado_clave', label: 'Nombre del asegurado clave', type: 'text', required: true },
          { key: 'rol_empresa', label: 'Rol en la empresa', type: 'text', required: true },
          { key: 'suma_asegurada', label: 'Suma asegurada', type: 'number', required: true },
          { key: 'beneficiario_empresa_socios', label: 'Beneficiario — la empresa o los socios', type: 'text', required: true },
          F_INVALIDEZ,
        ],
      },
    ],
  },
  qualitas: {
    label: 'Quálitas',
    products: [
      {
        group: 'Seguro de Auto',
        name: 'Auto RC / Limitada / Amplia / Amplia Plus',
        hasBeneficiarios: false,
        fields: [
          { key: 'nivel_cobertura', label: 'Nivel de cobertura', type: 'select', options: ['RC', 'Limitada', 'Amplia', 'Amplia Plus'], required: true },
          { key: 'marca', label: 'Marca', type: 'text', required: true },
          { key: 'modelo', label: 'Modelo', type: 'text', required: true },
          { key: 'anio', label: 'Año', type: 'number', required: true },
          { key: 'version', label: 'Versión', type: 'text', required: true },
          { key: 'vin', label: 'Número de serie (VIN)', type: 'text', required: true },
          { key: 'placas', label: 'Placas', type: 'text', required: true },
          { key: 'suma_asegurada_vehiculo', label: 'Suma asegurada del vehículo', type: 'number', required: true },
          { key: 'deducible_danos', label: 'Deducible por daños materiales', type: 'number', required: true },
          { key: 'deducible_robo_total', label: 'Deducible por robo total', type: 'number', required: true },
          { key: 'rc_mxn', label: 'Cobertura RC — MXN', type: 'number', required: true },
          { key: 'rc_usd_extranjero', label: 'Cobertura RC — USD extranjero', type: 'number', required: true },
          { key: 'gastos_medicos_ocupantes', label: 'Gastos médicos a ocupantes', type: 'number', required: true },
          { key: 'auto_sustituto', label: 'Auto sustituto — hasta 30 días', type: 'checkbox', required: false },
          { key: 'asistencia_vial', label: 'Asistencia vial — ilimitada o por eventos', type: 'text', required: false },
          { key: 'defensa_legal', label: 'Defensa legal — fianza hasta 1M', type: 'checkbox', required: false },
          { key: 'cobertura_llantas', label: 'Cobertura llantas y rines', type: 'checkbox', required: false },
          { key: 'robo_autopartes', label: 'Robo de autopartes', type: 'checkbox', required: false },
          { key: 'reparacion_agencia', label: 'Reparación en agencia', type: 'checkbox', required: false },
          { key: 'asistencia_satelital', label: 'Asistencia satelital', type: 'checkbox', required: false },
          { key: 'cobertura_usa', label: 'Cobertura en EE.UU. / CA / TX (Sin Fronteras)', type: 'checkbox', required: false },
        ],
      },
      {
        group: 'Seguro de Auto',
        name: 'Flotilla / Transporte / Camiones',
        hasBeneficiarios: false,
        fields: [
          { key: 'num_unidades', label: 'Número de unidades', type: 'number', required: true },
          { key: 'tipo_vehiculo', label: 'Tipo de vehículo', type: 'select', options: ['ligero', 'pesado', 'moto'], required: true },
          { key: 'uso_vehiculo', label: 'Uso del vehículo', type: 'select', options: ['particular', 'comercial', 'transporte público'], required: true },
          { key: 'suma_asegurada_unidad', label: 'Suma asegurada por unidad', type: 'number', required: true },
          { key: 'cobertura_rc', label: 'Cobertura RC', type: 'text', required: true },
          { key: 'rastreo_satelital', label: 'Rastreo satelital', type: 'checkbox', required: false },
          { key: 'materiales_peligrosos', label: 'Materiales peligrosos — cobertura especial', type: 'checkbox', required: false },
        ],
      },
    ],
  },
};

// Lookup rápido: name → productConfig
function getProductConfig(businessLine, productName) {
  const lineConfig = POLICY_CONFIG[businessLine];
  if (!lineConfig) return null;
  return lineConfig.products.find((p) => p.name === productName) || null;
}

// ─── Sección Beneficiarios ───────────────────────────────────────────────────
function BeneficiariosSection({ beneficiarios, setBeneficiarios }) {
  const add = () =>
    setBeneficiarios((prev) => [
      ...prev,
      { nombre: '', parentesco: '', porcentaje: '', fecha_nacimiento: '', tipo: 'preferente' },
    ]);

  const update = (index, field, value) =>
    setBeneficiarios((prev) =>
      prev.map((b, i) => (i === index ? { ...b, [field]: value } : b))
    );

  const remove = (index) =>
    setBeneficiarios((prev) => prev.filter((_, i) => i !== index));

  const total = beneficiarios.reduce(
    (sum, b) => sum + (parseFloat(b.porcentaje) || 0),
    0
  );

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="font-semibold text-sm">Beneficiarios</h3>
          {beneficiarios.length > 0 && (
            <p className={`text-xs mt-0.5 ${Math.abs(total - 100) < 0.01 ? 'text-green-600' : 'text-amber-600'}`}>
              Total asignado: {total}% {Math.abs(total - 100) < 0.01 ? '✓' : '(debe sumar 100%)'}
            </p>
          )}
        </div>
        <Button type="button" size="sm" variant="outline" onClick={add}>
          <Plus className="h-3.5 w-3.5 mr-1" />
          Agregar
        </Button>
      </div>

      {beneficiarios.length === 0 && (
        <p className="text-sm text-muted-foreground text-center py-3 border rounded-md border-dashed">
          Sin beneficiarios. Haz clic en "Agregar" para añadir.
        </p>
      )}

      {beneficiarios.map((b, i) => (
        <div key={i} className="border rounded-md p-3 space-y-2 bg-muted/30">
          <div className="grid grid-cols-2 gap-2">
            <div className="space-y-1">
              <Label className="text-xs">Nombre completo *</Label>
              <Input
                value={b.nombre}
                onChange={(e) => update(i, 'nombre', e.target.value)}
                className="h-8 text-sm"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">Parentesco *</Label>
              <Input
                value={b.parentesco}
                onChange={(e) => update(i, 'parentesco', e.target.value)}
                className="h-8 text-sm"
                placeholder="Cónyuge, Hijo, Padre..."
                required
              />
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 items-end">
            <div className="space-y-1">
              <Label className="text-xs">Porcentaje % *</Label>
              <Input
                type="number"
                value={b.porcentaje}
                onChange={(e) => update(i, 'porcentaje', e.target.value)}
                className="h-8 text-sm"
                min="0"
                max="100"
                required
              />
            </div>
            <div className="space-y-1">
              <Label className="text-xs">F. Nacimiento</Label>
              <Input
                type="date"
                value={b.fecha_nacimiento}
                onChange={(e) => update(i, 'fecha_nacimiento', e.target.value)}
                className="h-8 text-sm"
              />
            </div>
            <div className="flex gap-1 items-end">
              <div className="flex-1 space-y-1">
                <Label className="text-xs">Tipo</Label>
                <Select value={b.tipo} onValueChange={(v) => update(i, 'tipo', v)}>
                  <SelectTrigger className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="preferente">Preferente</SelectItem>
                    <SelectItem value="contingente">Contingente</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                className="h-8 w-8 text-red-500 hover:text-red-600 hover:bg-red-50"
                onClick={() => remove(i)}
              >
                <Trash2 className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Renderizador de campo dinámico ─────────────────────────────────────────
export function DynamicField({ field, value, onChange }) {
  const id = `dpf-${field.key}`;
  const label = (
    <Label htmlFor={id} className="text-sm">
      {field.label}
      {field.required && <span className="text-red-500 ml-0.5">*</span>}
    </Label>
  );

  if (field.type === 'checkbox') {
    return (
      <div className="flex items-center gap-2 py-1">
        <Checkbox
          id={id}
          checked={!!value}
          onCheckedChange={(checked) => onChange(checked)}
        />
        {label}
      </div>
    );
  }

  if (field.type === 'select') {
    return (
      <div className="space-y-1">
        {label}
        <Select value={value || ''} onValueChange={onChange}>
          <SelectTrigger id={id}>
            <SelectValue placeholder="Seleccionar..." />
          </SelectTrigger>
          <SelectContent>
            {field.options.map((opt) => (
              <SelectItem key={opt} value={opt}>
                {opt}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
    );
  }

  if (field.type === 'textarea') {
    return (
      <div className="space-y-1">
        {label}
        <Textarea
          id={id}
          value={value || ''}
          onChange={(e) => onChange(e.target.value)}
          placeholder={field.placeholder || ''}
          required={field.required}
          rows={2}
          className="text-sm"
        />
      </div>
    );
  }

  // text | number | date
  return (
    <div className="space-y-1">
      {label}
      <Input
        id={id}
        type={field.type}
        value={value ?? ''}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder || ''}
        required={field.required}
        className="text-sm"
        {...(field.type === 'number' ? { min: field.min ?? 0, ...(field.max != null ? { max: field.max } : {}), step: 'any' } : {})}
      />
    </div>
  );
}

// ─── Estado inicial del formulario base ──────────────────────────────────────
const EMPTY_FORM = {
  policy_number: '',
  business_line: '',
  product_type: '',
  client_id: '',
  client_name: '',
  agent_id: '',
  agent_name: '',
  premium: '',
  start_date: '',
  end_date: '',
  status: 'active',
  client_intention: 'no_definido',
  notes: '',
};

// ─── Componente principal ────────────────────────────────────────────────────
export function PolicyFormModal({ open, onOpenChange, editingPolicy, clients, agents, onSuccess, policyCustomFields = [] }) {
  const { user } = useAuth();
  const { availableLines: contextLines } = useBusinessLine();
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [detalles, setDetalles] = useState({});
  const [beneficiarios, setBeneficiarios] = useState([]);
  const [customValues, setCustomValues] = useState({});
  const [saving, setSaving] = useState(false);

  const isEditing = !!editingPolicy;

  const availableLines = contextLines.filter(l => l.id !== 'all' && l.id in POLICY_CONFIG)
    .map(l => ({ id: l.id, label: POLICY_CONFIG[l.id].label }));

  // Poblar formulario al abrir en modo edición
  useEffect(() => {
    if (!open) return;
    if (editingPolicy) {
      setFormData({
        policy_number: editingPolicy.policy_number || '',
        business_line: editingPolicy.business_line || 'allianz',
        product_type: editingPolicy.product_type || '',
        client_id: editingPolicy.client_id || '',
        client_name: editingPolicy.client_name || '',
        agent_id: editingPolicy.agent_id || '',
        agent_name: editingPolicy.agent_name || '',
        premium: editingPolicy.premium != null ? String(editingPolicy.premium) : '',
        start_date: editingPolicy.start_date || '',
        end_date: editingPolicy.end_date || '',
        status: editingPolicy.status || 'active',
        client_intention: editingPolicy.client_intention || 'no_definido',
        notes: editingPolicy.notes || '',
      });
      setDetalles(editingPolicy.detalles_producto || {});
      setBeneficiarios(editingPolicy.beneficiarios || []);
      setCustomValues(editingPolicy.custom_values || {});
    } else {
      const firstLine = availableLines.find(l => l.id !== 'all')?.id || 'allianz';
      setFormData({ ...EMPTY_FORM, business_line: firstLine, agent_id: user?.id || '', agent_name: user?.full_name || '' });
      setDetalles({});
      setBeneficiarios([]);
      setCustomValues({});
    }
  }, [open, editingPolicy]);

  const productConfig = getProductConfig(formData.business_line, formData.product_type);
  const hasBeneficiarios = productConfig?.hasBeneficiarios ?? false;
  const productFields = productConfig?.fields ?? [];

  // Separar campos en inputs normales y checkboxes
  const regularFields = productFields.filter((f) => f.type !== 'checkbox');
  const checkboxFields = productFields.filter((f) => f.type === 'checkbox');

  const set = useCallback((key, val) => setFormData((prev) => ({ ...prev, [key]: val })), []);
  const setDetallesField = useCallback((key, val) => setDetalles((prev) => ({ ...prev, [key]: val })), []);

  const handleClientChange = (clientId) => {
    const client = clients.find((c) => c.id === clientId);
    set('client_id', clientId);
    set('client_name', client?.name || '');
  };

  const handleAgentChange = (agentId) => {
    const agent = agents.find((a) => a.id === agentId);
    set('agent_id', agentId);
    set('agent_name', agent?.full_name || '');
  };

  const handleBusinessLineChange = (line) => {
    set('business_line', line);
    set('product_type', '');
    setDetalles({});
    setBeneficiarios([]);
  };

  const handleProductChange = (product) => {
    set('product_type', product);
    setDetalles({});
    setBeneficiarios([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (isEditing && formData.status === 'omitted') {
        await axios.post(`${API}/policies/mark-omitted`, { policy_ids: [editingPolicy.id], source: 'manual' });
        toast.success('Póliza marcada como omitida');
        onOpenChange(false);
        onSuccess();
        return;
      }

      if (isEditing && formData.status === 'rehabilitated') {
        await axios.post(`${API}/policies/${editingPolicy.id}/rehabilitate`, {});
        toast.success('Póliza rehabilitada correctamente');
        onOpenChange(false);
        onSuccess();
        return;
      }

      const payload = {
        ...formData,
        premium: parseFloat(formData.premium) || 0,
        owner_id: user?.id,
        detalles_producto: detalles,
        beneficiarios,
      };

      let savedPolicyId;
      if (isEditing) {
        await axios.put(`${API}/policies/${editingPolicy.id}`, payload);
        savedPolicyId = editingPolicy.id;
        toast.success('Póliza actualizada correctamente');
      } else {
        const res = await axios.post(`${API}/policies`, payload);
        savedPolicyId = res.data.id;
        toast.success('Póliza creada correctamente');
      }
      if (savedPolicyId && Object.values(customValues).some(v => v !== '')) {
        await axios.patch(`${API}/policies/${savedPolicyId}/custom-fields`, customValues);
      }
      onOpenChange(false);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar la póliza');
    } finally {
      setSaving(false);
    }
  };

  // Productos agrupados por categoría para el select
  const lineConfig = POLICY_CONFIG[formData.business_line];
  const productsByGroup = lineConfig
    ? lineConfig.products.reduce((acc, p) => {
        if (!acc[p.group]) acc[p.group] = [];
        acc[p.group].push(p.name);
        return acc;
      }, {})
    : {};

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditing ? 'Editar Póliza' : 'Nueva Póliza'}</DialogTitle>
          <DialogDescription>
            {isEditing ? 'Modifica los datos de la póliza' : 'Registra una nueva póliza de seguro'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* ── Sección base ── */}
          <div className="space-y-4">
            <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
              Datos de la póliza
            </h3>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="policy_number">
                  Número de póliza <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="policy_number"
                  value={formData.policy_number}
                  onChange={(e) => set('policy_number', e.target.value)}
                  placeholder="ALZ-2026-001"
                  required={formData.status !== 'omitted'}
                />
              </div>

              <div className="space-y-1">
                <Label htmlFor="business_line">
                  Aseguradora <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.business_line} onValueChange={handleBusinessLineChange}>
                  <SelectTrigger id="business_line">
                    <SelectValue placeholder="Seleccionar..." />
                  </SelectTrigger>
                  <SelectContent>
                    {availableLines.map((l) => (
                      <SelectItem key={l.id} value={l.id}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="space-y-1">
              <Label htmlFor="product_type">
                Tipo de producto <span className="text-red-500">*</span>
              </Label>
              <Input
                id="product_type"
                value={formData.product_type}
                onChange={(e) => handleProductChange(e.target.value)}
                placeholder="Ej. OptiMaxx Plus, Auto, Temporal..."
                required={formData.status !== 'omitted'}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="client_id">
                  Cliente <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.client_id} onValueChange={handleClientChange}>
                  <SelectTrigger id="client_id">
                    <SelectValue placeholder="Seleccionar cliente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {clients.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-1">
                <Label htmlFor="agent_id">
                  Agente <span className="text-red-500">*</span>
                </Label>
                <Select value={formData.agent_id} onValueChange={handleAgentChange}>
                  <SelectTrigger id="agent_id">
                    <SelectValue placeholder="Seleccionar agente..." />
                  </SelectTrigger>
                  <SelectContent>
                    {agents.map((a) => (
                      <SelectItem key={a.id} value={a.id}>
                        {a.full_name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-4">
              <div className="space-y-1">
                <Label htmlFor="premium">
                  Prima / Aportaciones (MXN) <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="premium"
                  type="number"
                  value={formData.premium}
                  onChange={(e) => set('premium', e.target.value)}
                  placeholder="15000"
                  min="0"
                  step="any"
                  required={formData.status !== 'omitted'}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="start_date">
                  Fecha de inicio <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => set('start_date', e.target.value)}
                  required={formData.status !== 'omitted'}
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="end_date">
                  Fecha de vencimiento <span className="text-red-500">*</span>
                </Label>
                <Input
                  id="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => set('end_date', e.target.value)}
                  required={formData.status !== 'omitted'}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1">
                <Label htmlFor="status">Estado</Label>
                <Select value={formData.status} onValueChange={(v) => set('status', v)}>
                  <SelectTrigger id="status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Activa</SelectItem>
                    <SelectItem value="pending">Pendiente</SelectItem>
                    <SelectItem value="pending_payment">Pendiente de Pago</SelectItem>
                    <SelectItem value="expired">Vencida</SelectItem>
                    <SelectItem value="cancelled">Cancelada</SelectItem>
                    <SelectItem value="renewed">Renovada</SelectItem>
                    <SelectItem value="rehabilitated">Rehabilitada</SelectItem>
                    <SelectItem value="omitted">Omitida</SelectItem>
                    <SelectItem value="no_renewed">No renovó</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1">
                <Label htmlFor="client_intention">Intención del cliente</Label>
                <Select value={formData.client_intention} onValueChange={(v) => set('client_intention', v)}>
                  <SelectTrigger id="client_intention">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="no_definido">No definido</SelectItem>
                    <SelectItem value="renovar">Renovar</SelectItem>
                    <SelectItem value="cancelar">Cancelar</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* ── Sección detalles del producto ── */}
          {productFields.length > 0 && (
            <>
              <Separator />
              <div className="space-y-4">
                <h3 className="font-semibold text-sm text-muted-foreground uppercase tracking-wide">
                  Detalles — {formData.product_type}
                </h3>

                {/* Campos normales en grid 2 col */}
                {regularFields.length > 0 && (
                  <div className="grid grid-cols-2 gap-4">
                    {regularFields.map((field) => (
                      <div
                        key={field.key}
                        className={field.type === 'textarea' ? 'col-span-2' : ''}
                      >
                        <DynamicField
                          field={field}
                          value={detalles[field.key]}
                          onChange={(val) => setDetallesField(field.key, val)}
                        />
                      </div>
                    ))}
                  </div>
                )}

                {/* Checkboxes en sección separada */}
                {checkboxFields.length > 0 && (
                  <div className="space-y-1">
                    <p className="text-xs text-muted-foreground font-medium mb-1">Coberturas adicionales</p>
                    <div className="grid grid-cols-2 gap-x-6 gap-y-1">
                      {checkboxFields.map((field) => (
                        <DynamicField
                          key={field.key}
                          field={field}
                          value={detalles[field.key]}
                          onChange={(val) => setDetallesField(field.key, val)}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ── Sección beneficiarios ── */}
          {hasBeneficiarios && (
            <>
              <Separator />
              <BeneficiariosSection
                beneficiarios={beneficiarios}
                setBeneficiarios={setBeneficiarios}
              />
            </>
          )}

          {/* ── Campos adicionales (custom) ── */}
          {policyCustomFields.length > 0 && (
            <>
              <Separator />
              <div className="space-y-3">
                <p className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                  Campos adicionales
                </p>
                <div className="grid grid-cols-2 gap-3">
                  {policyCustomFields.map(cf => (
                    <div key={cf.id} className={cf.field_type === 'lista' ? 'col-span-2' : ''}>
                      <Label className="text-xs">{cf.label}</Label>
                      {cf.field_type === 'texto' && (
                        <Input className="h-8 text-sm" value={customValues[cf.id] || ''} onChange={e => setCustomValues(p => ({ ...p, [cf.id]: e.target.value }))} />
                      )}
                      {cf.field_type === 'numero' && (
                        <Input type="number" className="h-8 text-sm" value={customValues[cf.id] || ''} onChange={e => setCustomValues(p => ({ ...p, [cf.id]: e.target.value }))} />
                      )}
                      {cf.field_type === 'fecha' && (
                        <Input type="date" className="h-8 text-sm" value={customValues[cf.id] || ''} onChange={e => setCustomValues(p => ({ ...p, [cf.id]: e.target.value }))} />
                      )}
                      {cf.field_type === 'booleano' && (
                        <Select value={customValues[cf.id] || ''} onValueChange={v => setCustomValues(p => ({ ...p, [cf.id]: v }))}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">Sí</SelectItem>
                            <SelectItem value="false">No</SelectItem>
                          </SelectContent>
                        </Select>
                      )}
                      {cf.field_type === 'lista' && (
                        <Select value={customValues[cf.id] || ''} onValueChange={v => setCustomValues(p => ({ ...p, [cf.id]: v }))}>
                          <SelectTrigger className="h-8 text-sm"><SelectValue placeholder="—" /></SelectTrigger>
                          <SelectContent>
                            {(cf.options || []).map(opt => <SelectItem key={opt} value={opt}>{opt}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}

          {/* ── Botones ── */}
          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
              disabled={saving}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              className="bg-[#1a365d] hover:bg-[#1a365d]/90"
              disabled={saving}
            >
              {saving && (
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
              )}
              {isEditing ? 'Guardar cambios' : 'Crear póliza'}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default PolicyFormModal;
